import { buildAsturHeaders } from '@/endpoints/channel/asturHeaders';
import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { WriteRequestOptions } from '../requestOptions';

// Type definitions
export interface ScheduleOverride {
  id: number;
  action_id: number;
  subscription_id: number;
  schedule_override: string;
  enabled: number;
  created_at: string;
  updated_at: string;
}

export interface ScheduleOverrideError {
  error: string;
  schedule?: string;
  calculated_interval_minutes?: number;
  minimum_interval_minutes?: number;
  message?: string;
}

const isScheduleOverrideError = (value: unknown): value is ScheduleOverrideError => {
  if (!value || typeof value !== 'object') return false;
  return typeof (value as { error?: unknown }).error === 'string';
};

export interface CreateScheduleOverrideRequest {
  enabled: number;
  schedule_override: string;
}

export interface UpdateScheduleOverrideRequest {
  enabled: number;
  schedule_override: string;
}

// Validation functions
const validateCreateParams = (actionId: number, subscriptionId: number, schedule: string) => {
  if (!actionId || actionId < 1) {
    throw new Error('Valid action ID is required');
  }
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
  if (!schedule || schedule.trim() === '') {
    throw new Error('Schedule is required');
  }
};

const validateUpdateParams = (overrideId: number, schedule: string) => {
  if (!overrideId || overrideId < 1) {
    throw new Error('Valid override ID is required');
  }
  if (!schedule || schedule.trim() === '') {
    throw new Error('Schedule is required');
  }
};

const validateDeleteParams = (overrideId: number) => {
  if (!overrideId || overrideId < 1) {
    throw new Error('Valid override ID is required');
  }
};

// Create schedule override (POST)
type CreateParams = {
  actionId: number;
  subscriptionId: number;
  schedule: string;
  enabled: boolean;
};

export type ScheduleOverrideRequestOptions = WriteRequestOptions;

export const createScheduleOverride = async (
  scope: RequestScope,
  { actionId, subscriptionId, schedule, enabled }: CreateParams,
  options: ScheduleOverrideRequestOptions
): Promise<ChannelApiResponse<ScheduleOverride>> => {
  try {
    validateCreateParams(actionId, subscriptionId, schedule);

    const requestBody: CreateScheduleOverrideRequest = {
      enabled: enabled ? 1 : 0,
      schedule_override: schedule,
    };

    const result = await scopedChannelApi(scope).post<ScheduleOverride | ScheduleOverrideError>(
      `astur/integration/action/override/${actionId}/${subscriptionId}`,
      requestBody,
      {
        ...options,
        headers: buildAsturHeaders({ subscriptionId }),
      }
    );

    // Check if response contains an error field (API returns 200 but with error object)
    if (isScheduleOverrideError(result.data)) {
      const errorData = result.data;
      const errorMessage = errorData.message || errorData.error || 'Failed to create schedule override';
      throw new Error(errorMessage);
    }

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

// Update schedule override (PUT)
type UpdateParams = {
  overrideId: number;
  schedule: string;
  enabled: boolean;
  subscriptionId?: number | string | null;
};

export const updateScheduleOverride = async (
  scope: RequestScope,
  { overrideId, schedule, enabled, subscriptionId }: UpdateParams,
  options: ScheduleOverrideRequestOptions
): Promise<ChannelApiResponse<ScheduleOverride>> => {
  try {
    validateUpdateParams(overrideId, schedule);

    const requestBody: UpdateScheduleOverrideRequest = {
      enabled: enabled ? 1 : 0,
      schedule_override: schedule,
    };

    const result = await scopedChannelApi(scope).put<ScheduleOverride | ScheduleOverrideError>(
      `astur/integration/action/override/${overrideId}`,
      requestBody,
      {
        ...options,
        headers: buildAsturHeaders({ subscriptionId }),
      }
    );

    // Check if response contains an error field (API returns 200 but with error object)
    if (isScheduleOverrideError(result.data)) {
      const errorData = result.data;
      const errorMessage = errorData.message || errorData.error || 'Failed to update schedule override';
      throw new Error(errorMessage);
    }

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

// Delete schedule override (DELETE)
type DeleteParams = {
  overrideId: number;
  subscriptionId?: number | string | null;
};

export const deleteScheduleOverride = async (
  scope: RequestScope,
  { overrideId, subscriptionId }: DeleteParams,
  options: ScheduleOverrideRequestOptions
): Promise<ChannelApiResponse<void>> => {
  try {
    validateDeleteParams(overrideId);

    const result = await scopedChannelApi(scope).delete<ScheduleOverrideError | Record<string, never>>(
      `astur/integration/action/override/${overrideId}`,
      {
        ...options,
        headers: buildAsturHeaders({ subscriptionId }),
      }
    );

    // Check if response contains an error field
    if (isScheduleOverrideError(result.data)) {
      const errorData = result.data;
      const errorMessage = errorData.message || errorData.error || 'Failed to delete schedule override';
      throw new Error(errorMessage);
    }

    return { data: undefined, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
